import { sql } from "drizzle-orm";
import { pgTable, text, varchar, uuid, timestamp, numeric, integer, pgEnum, boolean, jsonb } from "drizzle-orm/pg-core";
import { createInsertSchema, createSelectSchema } from "drizzle-zod";
import { z } from "zod";

// ============================================================================
// ISSUES MANAGER MODULE (MODULE 3.0 - SYSTEM OPERATIONS)
// ============================================================================

// Issue Severity Enum
export const issueSeverityEnum = pgEnum("issue_severity_enum", ["critical", "high", "medium", "low"]);

// Issue Status Enum
export const issueStatusEnum = pgEnum("issue_status_enum", [
  "open",
  "investigating",
  "fix_in_progress",
  "review",
  "user_final_verify",
  "closed",
  "wont_fix",
  "deferred",
  "duplicate",
  "rework"
]);

// Issue Category Enum
export const issueCategoryEnum = pgEnum("issue_category_enum", [
  "typescript_type",
  "ui_ux",
  "database_schema",
  "cache_state",
  "validation_form",
  "list_filter_search",
  "junction_table",
  "integration",
  "security",
  "performance",
  "documentation"
]);

// Issue Source Enum
export const issueSourceEnum = pgEnum("issue_source_enum", [
  "manual",
  "design_review_agent",
  "qa_review_agent",
  "playwright_import",
  "external"
]);

// Issue Priority Enum
export const issuePriorityEnum = pgEnum("issue_priority_enum", ["p0", "p1", "p2", "p3"]);

// Issue Environment Enum
export const issueEnvironmentEnum = pgEnum("issue_environment_enum", ["local", "ci", "staging", "production"]);

// Issue Module Enum (ISSUE-161: Convert from VARCHAR to ENUM)
export const issueModuleEnum = pgEnum("issue_module_enum", [
  "Client Manager",
  "Deal Builder",
  "Lender Manager",
  "Partner Manager",
  "Securities Manager",
  "Issues Manager",
  "Control Center",
  "Project Manager",
  "Infrastructure",
  "Security",
  "Security Manager",
  "Business Analytics"
]);

// Resolution Reason Enum
export const resolutionReasonEnum = pgEnum("resolution_reason_enum", [
  "fixed",
  "duplicate",
  "wont_fix",
  "cannot_reproduce",
  "by_design",
  "configuration_error"
]);

// Note Type Enum (for future Phase 3)
export const noteTypeEnum = pgEnum("note_type_enum", ["investigation", "solution", "comment", "workaround"]);

// Attachment Type Enum (SPRINT-1 Enhancement - Screenshot Upload System)
export const attachmentTypeEnum = pgEnum("attachment_type_enum", [
  "screenshot",
  "trace",
  "artifact",
  "video",
  "document"
]);

// Attachment Source Enum
export const attachmentSourceEnum = pgEnum("attachment_source_enum", [
  "manual_upload",
  "playwright_mcp",
  "design_review_agent",
  "qa_review_agent",
  "bulk_import"
]);

// Issues Table
export const issues = pgTable("crm_x_issues", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),

  // Core Identity
  issueNumber: varchar("issue_number", { length: 20 }).notNull().unique(),
  title: varchar("title", { length: 255 }).notNull(),
  description: text("description"),

  // Classification
  severity: issueSeverityEnum("severity").notNull(),
  priority: issuePriorityEnum("priority").notNull().default("p2"),
  status: issueStatusEnum("status").notNull().default("open"),
  category: issueCategoryEnum("category"),
  module: issueModuleEnum("module").notNull(),  // ISSUE-161: Changed from VARCHAR to ENUM
  environment: issueEnvironmentEnum("environment").notNull(),
  source: issueSourceEnum("source").notNull().default("manual"),

  // Technical Details
  errorMessage: text("error_message"),
  rootCause: text("root_cause"),
  solution: text("solution"),
  prevention: text("prevention"),

  // Testing Links
  testId: varchar("test_id", { length: 50 }),
  testFilePath: varchar("test_file_path", { length: 500 }),
  testLineNumber: integer("test_line_number"),
  screenshotPath: varchar("screenshot_path", { length: 500 }),

  // Sprint Context
  sprintId: varchar("sprint_id", { length: 20 }),

  // Assignment & Tracking
  assignedTo: uuid("assigned_to"),
  assignedAt: timestamp("assigned_at"),
  resolvedBy: uuid("resolved_by"),
  resolvedAt: timestamp("resolved_at"),
  assignedToAgent: varchar("assigned_to_agent", { length: 100 }),
  resolvedByAgent: varchar("resolved_by_agent", { length: 100 }),

  // Metadata (JSONB fields)
  tags: text("tags").array(),
  linkedCommits: text("linked_commits"), // JSONB stored as text in Drizzle
  sourceMetadata: text("source_metadata"), // JSONB stored as text in Drizzle

  // Audit Trail
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
  createdBy: uuid("created_by"),

  // Testing Checklist Integration (Version 2.0 - SPRINT-1.5)
  checklistItemId: uuid("checklist_item_id"),  // Links to testing checklist item

  // Workflow Metrics (Version 3.0 - SPRINT-2 Improvements)
  complexityLevel: varchar("complexity_level", { length: 20 }),     // 'simple' | 'complex' | 'sprint'
  classificationScore: integer("classification_score"),             // 0-50 (15 = threshold)
  reworkCount: integer("rework_count").default(0),                  // Auto-increment on rework
  executionMode: varchar("execution_mode", { length: 20 }),         // 'full' | 'fast_track' | 'lightweight'
  consoleErrorCount: integer("console_error_count"),                // From GATE-3.1
  testPassRate: numeric("test_pass_rate", { precision: 5, scale: 2 }), // From GATE-5
  blockerCount: integer("blocker_count"),                           // From Design Review
});

// Issue Activity Type Enum
export const issueActivityTypeEnum = pgEnum("issue_activity_type_enum", [
  "created",
  "updated",
  "status_changed",
  "severity_changed",
  "priority_changed",
  "assigned",
  "unassigned",
  "resolved",
  "closed",
  "reopened",
  "field_changed",
  "note_added",
  "linked_commit",
  "user_rejected"
]);

// Issue Activity Log Table
export const issueActivityLog = pgTable("crm_x_issue_activity_log", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),

  // Foreign Key to Issue
  issueId: uuid("issue_id").notNull().references(() => issues.id, { onDelete: "cascade" }),

  // Activity Details
  activityType: issueActivityTypeEnum("activity_type").notNull(),
  changedBy: uuid("changed_by"),  // NULL for system/agent actions
  changedByName: varchar("changed_by_name", { length: 255 }),  // Cached name for display

  // Field Change Details
  fieldName: varchar("field_name", { length: 100 }),
  oldValue: text("old_value"),
  newValue: text("new_value"),

  // Additional Context
  notes: text("notes"),
  metadata: text("metadata"),  // JSONB stored as text

  // Audit Trail
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

// Issue Attachments Table (SPRINT-1 Enhancement - Screenshot Upload System)
export const issueAttachments = pgTable("crm_x_issue_attachments", {
  // Primary Key
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),

  // Foreign Key to Issue
  issueId: uuid("issue_id").notNull().references(() => issues.id, { onDelete: "cascade" }),

  // File Identity
  fileName: varchar("file_name", { length: 255 }).notNull(),
  filePath: varchar("file_path", { length: 500 }).notNull().unique(),
  fileSize: integer("file_size").notNull(),
  mimeType: varchar("mime_type", { length: 100 }).notNull(),

  // Optional Image Metadata
  width: integer("width"),
  height: integer("height"),

  // Classification
  attachmentType: attachmentTypeEnum("attachment_type").notNull(),
  source: attachmentSourceEnum("source").notNull(),
  description: text("description"),
  tags: text("tags").array(),

  // Test Linkage
  linkedTestId: varchar("linked_test_id", { length: 100 }),

  // Audit Trail
  uploadedAt: timestamp("uploaded_at").defaultNow().notNull(),
  uploadedBy: uuid("uploaded_by"),

  // Future Supabase Storage Migration Fields
  supabaseBucketName: varchar("supabase_bucket_name", { length: 100 }),
  supabaseStoragePath: varchar("supabase_storage_path", { length: 500 }),
  publicUrl: varchar("public_url", { length: 500 }),
  migratedToSupabase: boolean("migrated_to_supabase").default(false),
  migratedAt: timestamp("migrated_at"),

  // Metadata (JSONB stored as text in Drizzle)
  sourceMetadata: text("source_metadata"),

  // Timestamps
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

// ============================================================================
// BATCH SESSION TRACKING (ISSUE-144)
// ============================================================================

// Batch Sessions Table - tracks batch-fix workflow execution metrics
export const batchSessions = pgTable("crm_x_batch_sessions", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),

  // Session Identity
  sessionId: varchar("session_id", { length: 50 }).notNull().unique(),
  sprintFilter: varchar("sprint_filter", { length: 100 }),

  // Timing
  startedAt: timestamp("started_at").notNull(),
  completedAt: timestamp("completed_at"),

  // Issue Counts
  totalIssues: integer("total_issues").notNull().default(0),
  issuesPassed: integer("issues_passed").default(0),
  issuesFailed: integer("issues_failed").default(0),
  firstTimeRight: integer("first_time_right").default(0),

  // Quality Metrics
  firstTimeRightRate: numeric("first_time_right_rate", { precision: 5, scale: 2 }),
  reworkRate: numeric("rework_rate", { precision: 5, scale: 2 }),

  // Performance Metrics
  totalDurationMin: numeric("total_duration_min", { precision: 8, scale: 2 }),
  avgTimePerIssueMin: numeric("avg_time_per_issue_min", { precision: 8, scale: 2 }),
  issuesPerHour: numeric("issues_per_hour", { precision: 8, scale: 2 }),

  // Testing Metrics
  designReviewPassRate: numeric("design_review_pass_rate", { precision: 5, scale: 2 }),
  totalTestsPassed: integer("total_tests_passed").default(0),
  totalTestsFailed: integer("total_tests_failed").default(0),

  // Context Efficiency
  contextUsageTokens: integer("context_usage_tokens"),
  contextEfficiency: varchar("context_efficiency", { length: 20 }),

  // Pattern Learning
  patternsCreated: integer("patterns_created").default(0),
  patternsReused: integer("patterns_reused").default(0),

  // Configuration
  testingLevel: varchar("testing_level", { length: 20 }),
  workflowVersion: varchar("workflow_version", { length: 20 }),

  // Detailed Data
  issuesData: jsonb("issues_data"),
  notes: text("notes"),

  // Audit Trail
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

// ============================================================================
// ZOD SCHEMAS & TYPES - Issues Manager
// ============================================================================

// Issues Manager Zod Schemas
export const insertIssueSchema = createInsertSchema(issues, {
  title: z.string().min(1, "Title is required").max(255, "Title must be less than 255 characters"),
  severity: z.enum(["critical", "high", "medium", "low"], { required_error: "Severity is required" }),
  module: z.enum([
    "Client Manager",
    "Deal Builder",
    "Lender Manager",
    "Partner Manager",
    "Securities Manager",
    "Issues Manager",
    "Control Center",
    "Project Manager",
    "Infrastructure",
    "Security",
    "Security Manager",
    "Business Analytics"
  ], { required_error: "Module is required" }),  // ISSUE-161: Updated to enum
  environment: z.enum(["local", "ci", "staging", "production"], { required_error: "Environment is required" }),
  sprintId: z.string().max(20, "Sprint ID must be less than 20 characters").optional(),
  // Workflow Metrics (ISSUE-077: Complete schema refinements for metrics tracking)
  complexityLevel: z.enum(["simple", "complex", "sprint"]).nullable().optional(),
  classificationScore: z.number().int().min(0).max(50).nullable().optional(),
  executionMode: z.enum(["full", "fast_track", "lightweight"]).nullable().optional(),
  reworkCount: z.number().int().min(0).nullable().optional(),  // DB has .default(0)
  consoleErrorCount: z.number().int().min(0).nullable().optional(),
  testPassRate: z.string().nullable().optional(),  // numeric type in DB, string in API
  blockerCount: z.number().int().min(0).nullable().optional(),
}).omit({ id: true, issueNumber: true, createdAt: true, updatedAt: true });

export const updateIssueSchema = insertIssueSchema.partial();
export const selectIssueSchema = createSelectSchema(issues);

// Issues Manager Types
export type InsertIssue = z.infer<typeof insertIssueSchema>;
export type UpdateIssue = z.infer<typeof updateIssueSchema>;
export type Issue = typeof issues.$inferSelect;

// Issue Activity Log Schemas
export const insertIssueActivitySchema = createInsertSchema(issueActivityLog).omit({ id: true, createdAt: true });
export const selectIssueActivitySchema = createSelectSchema(issueActivityLog);

// Issue Activity Log Types
export type InsertIssueActivity = z.infer<typeof insertIssueActivitySchema>;
export type IssueActivity = typeof issueActivityLog.$inferSelect;

// Issue Attachments Schemas (SPRINT-1 Enhancement)
export const insertIssueAttachmentSchema = createInsertSchema(issueAttachments).omit({ id: true, createdAt: true, updatedAt: true });
export const selectIssueAttachmentSchema = createSelectSchema(issueAttachments);

// Batch Sessions Schemas (ISSUE-144)
export const insertBatchSessionSchema = createInsertSchema(batchSessions, {
  sessionId: z.string().min(1).max(50, "Session ID must be less than 50 characters"),
  sprintFilter: z.string().max(100).nullable().optional(),
  startedAt: z.union([z.string(), z.date()]),
  completedAt: z.union([z.string(), z.date()]).nullable().optional(),
  totalIssues: z.number().int().min(0).default(0),
  issuesPassed: z.number().int().min(0).nullable().optional(),
  issuesFailed: z.number().int().min(0).nullable().optional(),
  firstTimeRight: z.number().int().min(0).nullable().optional(),
  firstTimeRightRate: z.string().nullable().optional(),
  reworkRate: z.string().nullable().optional(),
  totalDurationMin: z.string().nullable().optional(),
  avgTimePerIssueMin: z.string().nullable().optional(),
  issuesPerHour: z.string().nullable().optional(),
  designReviewPassRate: z.string().nullable().optional(),
  totalTestsPassed: z.number().int().min(0).nullable().optional(),
  totalTestsFailed: z.number().int().min(0).nullable().optional(),
  contextUsageTokens: z.number().int().min(0).nullable().optional(),
  contextEfficiency: z.string().max(20).nullable().optional(),
  patternsCreated: z.number().int().min(0).nullable().optional(),
  patternsReused: z.number().int().min(0).nullable().optional(),
  testingLevel: z.string().max(20).nullable().optional(),
  workflowVersion: z.string().max(20).nullable().optional(),
  issuesData: z.any().nullable().optional(),
  notes: z.string().nullable().optional(),
}).omit({ id: true, createdAt: true });

export const updateBatchSessionSchema = insertBatchSessionSchema.partial();
export const selectBatchSessionSchema = createSelectSchema(batchSessions);

// Batch Sessions Types
export type InsertBatchSession = z.infer<typeof insertBatchSessionSchema>;
export type UpdateBatchSession = z.infer<typeof updateBatchSessionSchema>;
export type BatchSession = typeof batchSessions.$inferSelect;

// Issue Attachments Types
export type InsertIssueAttachment = z.infer<typeof insertIssueAttachmentSchema>;
export type IssueAttachment = typeof issueAttachments.$inferSelect;
